import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Link2, Save } from "lucide-react";

export default function RingAdmin({ api }) {
  const [noms, setNoms] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/ring/nominations"); setNoms(data.nominations || []); }
    catch { toast.error("Couldn't load nominations."); setNoms([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const cur = noms && (noms.find((n) => n.id === sel) || null);

  const save = async (patch) => {
    setBusy("save");
    try { await api.patch(`/admin/ring/nominations/${cur.id}`, patch); toast.success("Saved."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(""); }
  };

  const genConsent = async (form_type) => {
    setBusy(form_type);
    try {
      const { data } = await api.post("/admin/ring/consents", { nomination_id: cur.id, form_type, display_as: cur.nominee });
      await navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success("Consent link created & copied", { description: data.url });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(""); }
  };

  if (noms === null) return <div className="mt-10 text-[#6E6E6E] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>;

  const field = "mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 text-sm focus:outline-none focus:border-[#2D2D2D]";
  const STATUSES = ["new", "selected", "revealed", "published", "archived"];

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6" data-testid="ring-admin">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-head font-semibold uppercase text-xl text-[#1E1E1E]">Nominations ({noms.length})</h2>
        </div>
        <div className="space-y-2 max-h-[70vh] overflow-auto">
          {noms.length === 0 && <p className="text-sm text-[#6E6E6E]">No nominations yet.</p>}
          {noms.map((n) => (
            <button key={n.id} onClick={() => setSel(n.id)} data-testid={`ring-nom-${n.id}`}
              className={`w-full text-left border-2 p-3 transition-colors ${cur?.id === n.id ? "border-[#2D2D2D] bg-[#F3ECDD]" : "border-[#D3CFC0] bg-[#F3F1EC] hover:border-[#2D2D2D]"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-head text-[#1E1E1E] text-sm truncate">{n.nominee}</span>
                <span className="kicker text-[#DB9130]">{n.status}{n.ring_number ? ` · #${n.ring_number}` : ""}</span>
              </div>
              <p className="text-xs text-[#6E6E6E] mt-1 line-clamp-2">{n.story}</p>
            </button>
          ))}
        </div>
      </div>

      {cur ? (
        <div className="border-2 border-[#1E1E1E] bg-[#F3F1EC] p-5" data-testid="ring-detail">
          <div className="flex gap-4">
            {cur.photo_url && <img src={cur.photo_url} alt="" className="w-24 h-24 object-cover border-2 border-[#D3CFC0]" />}
            <div className="min-w-0">
              <h3 className="font-head font-semibold text-lg text-[#1E1E1E]">{cur.nominee}</h3>
              <p className="text-xs text-[#6E6E6E] mt-1">Nominated by {cur.nominator_name} · {cur.nominator_contact}</p>
            </div>
          </div>
          <div className="mt-4 space-y-3 text-sm">
            <div><span className="kicker text-[#6E6E6E]">The moment</span><p className="text-[#1E1E1E] whitespace-pre-wrap">{cur.story}</p></div>
            <div><span className="kicker text-[#6E6E6E]">What stops if they disappear</span><p className="text-[#1E1E1E] whitespace-pre-wrap">{cur.impact}</p></div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div>
              <label className="kicker">Status</label>
              <select defaultValue={cur.status} onChange={(e) => save({ status: e.target.value })} data-testid="ring-status" className={field}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="kicker">Ring number</label><input defaultValue={cur.ring_number || ""} onBlur={(e) => e.target.value !== (cur.ring_number || "") && save({ ring_number: e.target.value })} data-testid="ring-number" className={field} placeholder="001" /></div>
            <div className="col-span-2"><label className="kicker">City / State</label><input defaultValue={cur.city_state || ""} onBlur={(e) => e.target.value !== (cur.city_state || "") && save({ city_state: e.target.value })} className={field} /></div>
            <div className="col-span-2"><label className="kicker">Pull-quote</label><textarea defaultValue={cur.pull_quote || ""} onBlur={(e) => e.target.value !== (cur.pull_quote || "") && save({ pull_quote: e.target.value })} data-testid="ring-quote" rows={2} className={field} /></div>
            <div className="col-span-2"><label className="kicker">Recognition video URL</label><input defaultValue={cur.video_url || ""} onBlur={(e) => e.target.value !== (cur.video_url || "") && save({ video_url: e.target.value })} className={field} placeholder="https://youtube.com/..." /></div>
          </div>

          <div className="mt-5 border-t-2 border-[#D3CFC0] pt-4">
            <p className="text-xs text-[#8A6A2E] bg-[#F3ECDD] border border-[#B08D57] px-2 py-1 mb-3">Consent forms: draft language — pending legal review before first signature.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => genConsent("media_release")} disabled={busy === "media_release"} data-testid="ring-consent-media" className="inline-flex items-center gap-2 border-2 border-[#2D2D2D] text-[#2D2D2D] font-head uppercase text-xs px-4 py-2 hover:bg-[#2D2D2D] hover:text-white transition-colors"><Link2 size={14} /> Media Release link</button>
              <button onClick={() => genConsent("guardian")} disabled={busy === "guardian"} data-testid="ring-consent-guardian" className="inline-flex items-center gap-2 border-2 border-[#2D2D2D] text-[#2D2D2D] font-head uppercase text-xs px-4 py-2 hover:bg-[#2D2D2D] hover:text-white transition-colors"><Link2 size={14} /> Guardian Consent link</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-[#D3CFC0] bg-[#F5F0EA] p-10 text-center text-[#6E6E6E]">Select a nomination to review, publish, and generate consent links.</div>
      )}
    </div>
  );
}
